import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, FileText, UserPlus, Send } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { postAdmission } from "../lib/api";

const STEPS = [
  { n: 1, title: "Visit or Apply Online", text: "Collect the application form from the college office or apply online here." },
  { n: 2, title: "Submit Documents", text: "Submit your 10+2 or graduation marksheet, ID proof, and recent photographs." },
  { n: 3, title: "Merit & Counseling", text: "Candidates will be shortlisted on merit; counseling dates will be notified." },
  { n: 4, title: "Fee & Enrollment", text: "Complete the fee payment process and receive your admission confirmation." },
];

export default function Admission() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    course_interested: "",
    previous_qualification: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.course_interested) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await postAdmission(form);
      setSuccess(true);
      toast.success("Application submitted. We will reach out to you soon!");
      setForm({ name: "", email: "", phone: "", course_interested: "", previous_qualification: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admission-page">
      <PageHeader
        eyebrow="Admissions 2026-27"
        title="Join a community that believes in you."
        subtitle="Applications are open for our B.A. and M.A. programs. Begin your journey with a heritage institution."
      />

      {/* Steps */}
      <section className="py-16 lg:py-24 bg-brand-ivory" data-testid="admission-steps">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <div className="eyebrow mb-2">How to apply</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary">
              Four simple steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white rounded-3xl p-6 border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.05)]"
                data-testid={`step-${s.n}`}
              >
                <div className="font-display text-5xl text-brand-secondary leading-none mb-3">
                  0{s.n}
                </div>
                <h3 className="font-serif text-xl text-brand-primary mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-brand-ink/75 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 lg:py-24 bg-brand-accent/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl overflow-hidden border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.06)]">
            <div className="grid lg:grid-cols-5">
              <div className="lg:col-span-2 bg-brand-primary text-white p-8 lg:p-10">
                <UserPlus className="w-8 h-8 text-brand-secondary mb-4" />
                <div className="eyebrow text-brand-secondary mb-2">Admission Enquiry</div>
                <h2 className="font-serif text-3xl mb-4">
                  Start your <br /> application.
                </h2>
                <p className="text-white/80 text-sm leading-relaxed mb-6">
                  Fill in your details below and our admission team will reach
                  out with next steps.
                </p>
                <div className="space-y-3 text-sm text-white/80">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-brand-secondary" /> Quick response within 24 hours
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-brand-secondary" /> Zero registration fee
                  </div>
                </div>
              </div>

              <form
                onSubmit={onSubmit}
                data-testid="admission-form"
                className="lg:col-span-3 p-8 lg:p-10 space-y-5"
              >
                {success ? (
                  <div className="min-h-[400px] flex flex-col items-center justify-center text-center" data-testid="admission-success">
                    <div className="w-16 h-16 rounded-full bg-brand-secondary/20 text-brand-primary flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="font-serif text-2xl text-brand-primary mb-2">
                      Application Received!
                    </h3>
                    <p className="text-brand-muted text-sm max-w-sm mb-6">
                      Thank you. Our admissions team will contact you within 24 hours at the provided email/phone.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSuccess(false)}
                      data-testid="submit-another-btn"
                      className="btn-outline"
                    >
                      Submit Another
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="adm-name">Full Name *</Label>
                        <Input
                          id="adm-name"
                          name="name"
                          data-testid="adm-name"
                          value={form.name}
                          onChange={onChange}
                          placeholder="Your full name"
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="adm-phone">Phone *</Label>
                        <Input
                          id="adm-phone"
                          name="phone"
                          data-testid="adm-phone"
                          value={form.phone}
                          onChange={onChange}
                          placeholder="10-digit mobile"
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="adm-email">Email *</Label>
                      <Input
                        id="adm-email"
                        name="email"
                        type="email"
                        data-testid="adm-email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="you@example.com"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Course Interested *</Label>
                        <Select
                          value={form.course_interested}
                          onValueChange={(v) => setForm({ ...form, course_interested: v })}
                        >
                          <SelectTrigger data-testid="adm-course" className="mt-1">
                            <SelectValue placeholder="Select a course" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BA - Urdu">B.A. — Urdu</SelectItem>
                            <SelectItem value="BA - Hindi">B.A. — Hindi</SelectItem>
                            <SelectItem value="BA - Medieval History">B.A. — Medieval History</SelectItem>
                            <SelectItem value="BA - Sociology">B.A. — Sociology</SelectItem>
                            <SelectItem value="BA - Sanskrit">B.A. — Sanskrit</SelectItem>
                            <SelectItem value="BA - Education">B.A. — Education</SelectItem>
                            <SelectItem value="BA - Economics">B.A. — Economics</SelectItem>
                            <SelectItem value="BA - English">B.A. — English</SelectItem>
                            <SelectItem value="BA - Fashion Designing">B.A. — Fashion Designing</SelectItem>
                            <SelectItem value="MA - Urdu">M.A. — Urdu</SelectItem>
                            <SelectItem value="MA - Medieval History">M.A. — Medieval History</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="adm-qual">Previous Qualification</Label>
                        <Input
                          id="adm-qual"
                          name="previous_qualification"
                          data-testid="adm-qual"
                          value={form.previous_qualification}
                          onChange={onChange}
                          placeholder="e.g. 12th — UP Board (2025)"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="adm-msg">Message (optional)</Label>
                      <Textarea
                        id="adm-msg"
                        name="message"
                        data-testid="adm-message"
                        value={form.message}
                        onChange={onChange}
                        placeholder="Any questions or information you'd like to share..."
                        rows={4}
                        className="mt-1"
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="adm-submit"
                      disabled={submitting}
                      className="btn-brand w-full sm:w-auto disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : (
                        <>Submit Application <Send className="w-4 h-4" /></>
                      )}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
